// Apolo IDE — useGitContext (app/hook, sprint composer-anchor E1).
//
// Orquestra a I/O (IPC do Electron) e entrega o veredito do core puro
// `resolveGitContext`. É a ÚNICA ponte entre o git vivo do diretório e a UI do
// composer — a pref salva entra só como input. Sem bridge (web/jsdom) degrada
// pra um contexto honesto (origin null), nunca cai silenciosamente na pref.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { resolveGitContext } from '../lib/gitContext.js';

async function ipcCurrentBranch(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.currentBranch) return null;
  try { return await window.apolo.git.currentBranch({ root }); } catch { return null; }
}
async function ipcOriginRemote(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.originRemote) return null;
  try { return await window.apolo.git.originRemote({ root }); } catch { return null; }
}
async function ipcDetect() {
  if (!hasElectronBridge() || !window.apolo?.repo?.detect) return null;
  try { return await window.apolo.repo.detect(); } catch { return null; }
}

/**
 * @param {{ root?: (string|null), selectedRepo?: object|null, detectLocal?: boolean }} params
 *   root = diretório da sessão (worktree). Vazio na home → usa repo.detect.
 *   selectedRepo = ui.github_repo (intenção; só INPUT pro core).
 *   detectLocal = quando false, NÃO faz o fallback `repo.detect()` da pasta do app.
 *     No scope GitHub o alvo é o repo SELECIONADO (clonado); detectar a pasta local
 *     (ex: ~/Apolo) e compará-la com a pref vira um "conflito" FALSO que bloqueia o
 *     envio toda vez. Default true (scope local / home mantém o detect útil).
 */
export function useGitContext({ root = null, selectedRepo = null, detectLocal = true } = {}) {
  const electronOk = hasElectronBridge();
  const [phase, setPhase] = useState('idle'); // idle|loading|ready
  const [live, setLive] = useState({ dir: null, liveOrigin: null, liveBranch: null });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!electronOk) {
      if (mountedRef.current) {
        setLive({ dir: null, liveOrigin: null, liveBranch: null });
        setPhase('ready');
      }
      return;
    }
    setPhase('loading');
    let dir = root || null;
    if (!dir && detectLocal) {
      const det = await ipcDetect();
      if (det?.ok && typeof det.path === 'string' && det.path.startsWith('/')) dir = det.path;
    }
    if (!dir) {
      if (mountedRef.current) {
        setLive({ dir: null, liveOrigin: null, liveBranch: null });
        setPhase('ready');
      }
      return;
    }
    const [originRes, branchRes] = await Promise.all([
      ipcOriginRemote(dir),
      ipcCurrentBranch(dir),
    ]);
    if (!mountedRef.current) return;
    setLive({
      dir,
      liveOrigin: originRes?.ok
        ? { ok: true, owner: originRes.owner, repo: originRes.repo, url: originRes.url }
        : null,
      liveBranch: branchRes?.ok ? (branchRes.branch || null) : null,
    });
    setPhase('ready');
  }, [electronOk, root, detectLocal]);

  useEffect(() => { void load(); }, [load]);

  const context = useMemo(
    () => resolveGitContext({
      selectedRepo,
      dir: live.dir,
      liveOrigin: live.liveOrigin,
      liveBranch: live.liveBranch,
    }),
    [selectedRepo, live],
  );

  return { context, phase, refresh: load, electronOk };
}
